<template>
  <div class="group-buying-details">
    <b-loading :active="loading" />
    <div class="column has-background-white px-4 py-3">
      <div class="flex items-center justify-between">
        <router-link class="prev-page" to="/statistics/group_buyings">
          <font-awesome-icon class="mr-2" icon="chevron-left" />
          <span>返回訂購列表</span>
        </router-link>
        <span class="text-gray-500 text-xs">*此頁面統計資訊包含訪客數據</span>
      </div>
      <h1 class="font-medium has-text-black is-size-5 mb-4 mt-4">
        訂購詳情
      </h1>

      <section class="flex flex-wrap gap-4 mb-4">
        <section id="summary" class="order__info-card summary">
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              訂購ID
            </label>
            <p class="order__info-item__content">
              {{ orderId }}
            </p>
          </div>
          <div class="order__info-item max-w-[230px]">
            <label class="order__info-item__label">
              訂購名稱
            </label>
            <p id="name" class="order__info-item__content tooltips">
              {{ groupBuyingInfo.name }}
              <b-tooltip
                :active="overLengthMap['name']"
                :label="groupBuyingInfo.name"
                append-to-body
                position="is-top"
                type="is-dark">
                {{ groupBuyingInfo.name }}
              </b-tooltip>
            </p>
          </div>
          <div class="order__info-item max-w-[188px]">
            <label class="order__info-item__label">
              店家名稱
            </label>
            <p id="store-name" class="order__info-item__content tooltips">
              {{ groupBuyingInfo.storeName }}
              <b-tooltip
                :active="overLengthMap['store-name']"
                :label="groupBuyingInfo.storeName"
                append-to-body
                position="is-top"
                type="is-dark">
                {{ groupBuyingInfo.storeName }}
              </b-tooltip>
            </p>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              訂購狀態
            </label>
            <p class="order__info-item__content">
              {{ statusText }}
            </p>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              開放訂購日
            </label>
            <p class="order__info-item__content">
              {{ startTimeText }}
            </p>
          </div>
          <div class="order__info-item center max-w-[160px]">
            <label class="order__info-item__label">
              領取日期
            </label>
            <div class="order__info-item__content">
              <b-dropdown
                v-if="receiveTimes.length > 2"
                append-to-body
                position="is-bottom-left"
                class="receive-time"
              >
                <template #trigger="{ active }">
                  <span>
                    {{ receiveTimes.slice(0, 2).join('、') }}
                  </span>
                  <font-awesome-icon v-if="active" icon="caret-up" />
                  <font-awesome-icon v-else icon="caret-down" />
                </template>
                <b-dropdown-item
                  v-for="item in receiveDropdownItems"
                  :key="item"
                  :focusable="false"
                  custom
                >
                  <span class="text-sm">{{ item }}</span>
                </b-dropdown-item>
              </b-dropdown>
              <span v-else >{{ receiveTimes.join('、') }}</span>
            </div>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              每人補助金額
            </label>
            <p class="order__info-item__content">
              {{ (this.groupBuyingInfo.subsidy || 0) | commaFormat | priceFormat }}
            </p>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              訂購總金額
            </label>
            <p class="order__info-item__content">
              {{ this.groupBuyingInfo.total | commaFormat | priceFormat }}
            </p>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              補助總金額
            </label>
            <p class="order__info-item__content">
              {{ this.groupBuyingInfo.subsidyTotal | commaFormat | priceFormat }}
            </p>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              總自付額
            </label>
            <p class="order__info-item__content">
              {{ this.groupBuyingInfo.outOfSubsidy | commaFormat | priceFormat }}
            </p>
          </div>
        </section>
        <section id="receive" class="order__info-card receive">
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              訂單數
            </label>
            <p class="order__info-item__content">
              {{ groupBuyingInfo.orderCount | commaFormat }}
            </p>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              已領取訂單
            </label>
            <div class="order__info-item__content">
              {{ groupBuyingInfo.received | commaFormat }}
            </div>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              未領取訂單
            </label>
            <p class="order__info-item__content">
              {{ groupBuyingInfo.unreceived | commaFormat }}
            </p>
          </div>
          <div class="order__info-item center no-grow w-[120px]">
            <label class="order__info-item__label">
              未付款人數
            </label>
            <p class="order__info-item__content">
              {{ groupBuyingInfo.unpaidCount | commaFormat }}
            </p>
          </div>
        </section>
      </section>
      <section class="filter flex items-center justify-between mb-4">
        <div class="flex gap-6">
          <b-input
            v-model="filter"
            placeholder="搜尋人員編號、帳號、姓名"
            class="filter__input"
            @keypress.enter.native="onSearch" />
          <w-button
            :disabled="!filter"
            size="sm"
            @click="onSearch">
            搜尋
          </w-button>
          <w-button
            color-type="secondary"
            size="sm"
            type="link"
            @click="onReset"
          >
            清除
          </w-button>
          <w-button
            size="sm"
            type="link"
            @click="openedFilter = !openedFilter"
          >
          進階篩選
            <i
              :class="openedFilter ? 'icon-up-chevron' : 'icon-down-chevron'"
              class="ml-2"
            />
          </w-button>
        </div>
        <div class="flex items-center justify-end">
          <b-dropdown aria-role="list" class="export-reports">
          <template #trigger>
            <w-button
              :disabled="disableExport"
              :loading="isExporting"
              color-type="secondary"
              size="func"
              type="link"
            >
              <span class="flex items-center">
                <i
                  :class="disableExport ? 'text-gray-300' : 'text-secondary-500'"
                  class="icon-cloud-download mr-2 text-base"
                />
                匯出報表
              </span>
            </w-button>
          </template>

          <b-dropdown-item
            aria-role="listitem"
            @click="exportStatisticsReport"
          >
            <span class="text-xs">品項統計</span>
          </b-dropdown-item>
          <b-dropdown-item
            aria-role="listitem"
            @click="exportOrdersReport"
          >
            <span class="text-xs">人員訂購列表</span>
          </b-dropdown-item>
          <b-dropdown-item
            aria-role="listitem"
            @click="exportOrdersReport(false)"
          >
            <span class="text-xs">訂購核銷列表</span>
          </b-dropdown-item>
        </b-dropdown>
        </div>
      </section>
      <div v-if="openedFilter" class="bg-light-gray-400 flex mb-4 px-4 py-2 rounded">
        <div class="flex flex-wrap flex-grow gap-y-4 item-center">
          <div class="flex items-center">
            <label class="flex-shrink-0 mr-4 text-gray-500 text-sm">領取狀態</label>
            <span
              v-for="item in filterTagItems"
              :class="{ active: receiveTypes.includes(item.value) }"
              :key="`selection-filter-${item.value}-${item.type}`"
              :title="item.label"
              class="selection-item"
              @click="onSelectItem(item)"
            >
              {{ item.label }}
            </span>
          </div>
          <div class="border-light-gray-500 border-r mx-6" />
          <div class="flex flex-grow items-baseline">
            <label class="flex-shrink-0 mr-4 text-gray-500 text-sm">部門</label>
            <w-select
              v-model="advancedSearchUnits"
              :options="userUnits"
              class="flex-grow max-w-[400px]"
              multiple
              placeholder="請選擇部門"
              show-checked-icon
              @change="advancedSearchUnitsChange"
            />
          </div>
        </div>
      </div>
      <b-table
        :data="formattedList"
        :row-class="itemRowClass"
        :mobile-cards="false"
        :opened-detailed="openedDetails"
        :show-detail-icon="false"
        class="details-table"
        custom-detail-row
        detail-key="index"
        detailed
        hoverable
        scrollable
      >
        <b-table-column
          :th-attrs="columnThAttrs"
          field="id"
          header-class="is-size-7"
          label="No."
          width="28px"
          v-slot="props"
        >
          {{ props.row.index + 1 + (page - 1) * perPage }}.
        </b-table-column>
        <b-table-column
          :centered="column.centered"
          :field="column.field"
          :key="`order-column-${column.field}`"
          :label="column.label"
          :numeric="column.numeric"
          :th-attrs="columnThAttrs"
          :width="column.width"
          header-class="is-size-7"
          v-for="column in columns"
          v-slot="props"
        >
          <span
            v-if="column.field === 'receivingStatus'"
            :class="{
            'text-pink-500':
              props.row[column.field] === receivingStatus.UNRECEIVED
            }"
            class="font-small"
          >
            <span>{{ props.row.receiveStatusText }}</span>
            <span v-if="props.row.receiveTime" class="text-gray-500 text-xs">
              {{ props.row.receiveTime }}
            </span>
          </span>
          <template v-else-if="column.field === 'onSiteOrder'">
            <i
              v-if="props.row[column.field]"
              class="icon-check-thin text-xl text-red-500"
            />
          </template>
          <span v-else class="font-small">{{ props.row[column.field] }}</span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="action"
          label="訂購內容"
          width="115px"
          centered
          header-class="is-size-7 py-2"
          v-slot="props"
        >
          <span class="cursor-pointer font-small hover:text-blue-500 text-blue-400"
            @click.prevent="props.toggleDetails(props.row)">
              {{ openedDetails.includes(props.row.index) ? '收合' : '展開' }}
          </span>
        </b-table-column>

        <template #detail="props">
          <tr>
            <td class="p-0" colspan="12">
              <table cellpadding="6" class="detail-table">
                <thead>
                  <tr>
                    <th width="60px">&nbsp;</th>
                    <th>數量</th>
                    <th>品項</th>
                    <th>備註</th>
                    <th>單價</th>
                    <th>小計</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, itemIndex) in getOrderDetails(props.row.orderItems)"
                    :key="`row-${props.row.id}-item-${itemIndex}`"
                  >
                    <td width="54px" />
                    <td>
                      <span class="text-primary-700">{{ item.quantity || '0' }}</span>
                    </td>
                    <td>
                      <span :title="item.name" class="text-primary-700">
                        {{ item.name }}
                      </span>
                    </td>
                    <td>
                      <template v-for="option in item.options">
                        <span
                          v-for="optionItem in option.items"
                          :key="`option-item-${optionItem.index}`"
                          class="
                            bg-blue-50
                            border
                            border-blue-100
                            last:mr-1
                            leading-[22px]
                            mr-1
                            px-[10px]
                            py-[1px]
                            rounded-full
                            whitespace-nowrap
                          "
                        >
                          {{ optionItem.name }}
                          {{ optionItem.price ? formatPrice(optionItem.price) : '' }}
                        </span>
                      </template>
                      <span v-if="item.note">#{{ item.note }}</span>
                    </td>
                    <td class="text-primary-700">{{ item.price | commaFormat | priceFormat }}</td>
                    <td class="text-primary-700">
                      {{ item.subtotal | commaFormat | priceFormat }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </template>

        <template #empty>
          <div v-if="!loading" class="my-5 has-text-centered">
            目前沒有任何訂購喔！
          </div>
        </template>
      </b-table>
      <div v-if="total && !loading" class="flex gap-4 items-center">
        <b-select v-model="perPage">
          <option
            v-for="option in perPageOptions"
            :value="option.value"
            :key="option.label"
          >
            {{ option.label }}
          </option>
        </b-select>
        <div class="flex-grow">
          <b-pagination
            :total="total"
            v-model="page"
            range-before="3"
            range-after="1"
            nextIcon="right-chevron"
            prevIcon="left-chevron"
            :per-page="perPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GroupBuyingDetails from './GroupBuyingDetails';

export default GroupBuyingDetails;
</script>

<style lang="scss" scoped>
@import 'GroupBuyingDetails';
</style>
